/**
 * Activités visées par une solution — divisions NAF et domaines métier.
 *
 * Même vocabulaire que les secteurs attribués aux commerciaux (Équipe) : une
 * division NAF à deux chiffres couvre tous ses codes, un code complet ne
 * couvre que lui, un domaine du catalogue couvre ses codes (« Fleuriste »
 * réunit 47.76Z, 01.19Z…). Sans sélection, la solution s'adresse à tout le
 * monde — c'est dit en clair, pas déduit d'une liste vide.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, X, Plus } from "lucide-react";
import api from "@/lib/crmApi";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const PREFIX_RE = /^\d{2}(\.?\d{2}[A-Z]?)?$/;

export default function ActivityPicker({ value, onChange }) {
  const prefixes = value?.naf_prefixes || [];
  const domaines = value?.domaines || [];
  const [choices, setChoices] = useState({ divisions: [], domaines: [] });
  const [filter, setFilter] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    api.get("/solutions/activites").then(({ data }) => setChoices(data)).catch(() => {});
  }, []);

  const divisionLabel = useMemo(() => {
    const map = new Map(choices.divisions.map((d) => [d.code, d.label]));
    return (code) => map.get(code) || code;
  }, [choices.divisions]);
  const domaineLabel = useMemo(() => {
    const map = new Map(choices.domaines.map((d) => [d.slug, d.label]));
    return (slug) => map.get(slug) || slug;
  }, [choices.domaines]);

  const setPrefixes = (next) => onChange({ naf_prefixes: next, domaines });
  const setDomaines = (next) => onChange({ naf_prefixes: prefixes, domaines: next });
  const togglePrefix = (code) =>
    setPrefixes(prefixes.includes(code) ? prefixes.filter((c) => c !== code) : [...prefixes, code]);
  const toggleDomaine = (slug) =>
    setDomaines(domaines.includes(slug) ? domaines.filter((d) => d !== slug) : [...domaines, slug]);

  const addManual = () => {
    const code = manual.trim().toUpperCase();
    if (!PREFIX_RE.test(code)) return toast.error("Code NAF invalide — ex. 56, 56.10 ou 56.10C");
    if (!prefixes.includes(code)) setPrefixes([...prefixes, code]);
    setManual("");
  };

  const q = norm(filter.trim());
  const visible = choices.divisions.filter((d) => !q || norm(`${d.code} ${d.label}`).includes(q));
  const total = prefixes.length + domaines.length;

  return (
    <div data-testid="activity-picker" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">Activités visées</span>
        <span className="font-mono text-xs text-slate-400">{total} sélectionnée(s)</span>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        {total === 0
          ? "Sans sélection, la solution s'adresse à toutes les activités."
          : "La solution sera proposée aux entreprises de ces activités."}
      </p>

      {total > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {domaines.map((slug) => (
            <span key={`d-${slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 py-1 pl-2.5 pr-1.5 text-xs text-violet-700">
              {domaineLabel(slug)}
              <button type="button" onClick={() => toggleDomaine(slug)} aria-label={`Retirer ${domaineLabel(slug)}`}
                className="text-violet-400 hover:text-violet-700"><X size={12} /></button>
            </span>
          ))}
          {prefixes.map((code) => (
            <span key={`p-${code}`} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 py-1 pl-2.5 pr-1.5 text-xs text-indigo-700">
              <span className="font-mono">{code}</span>
              {code.length === 2 && <span className="max-w-[160px] truncate">{divisionLabel(code).replace(/^\d{2} - /, "")}</span>}
              <button type="button" onClick={() => togglePrefix(code)} aria-label={`Retirer ${code}`}
                className="text-indigo-400 hover:text-indigo-700"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {choices.domaines.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.08em] text-slate-400">Domaines métier</div>
          <div className="flex flex-wrap gap-1.5">
            {choices.domaines.map((d) => {
              const on = domaines.includes(d.slug);
              return (
                <button key={d.slug} type="button" data-testid={`domaine-${d.slug}`} aria-pressed={on}
                  onClick={() => toggleDomaine(d.slug)} title={d.codes.join(", ")}
                  className={`rounded-lg border px-2.5 py-1 text-[12.5px] ${on
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.08em] text-slate-400">Divisions NAF</div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5">
          <Search size={14} className="shrink-0 text-slate-400" />
          <input data-testid="activity-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrer par code ou libellé…" className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
        </div>
        <div className="mt-2 max-h-44 space-y-0.5 overflow-y-auto">
          {visible.map((d) => (
            <label key={d.code} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-white">
              <input type="checkbox" data-testid={`division-${d.code}`} checked={prefixes.includes(d.code)}
                onChange={() => togglePrefix(d.code)} />
              <span className="shrink-0 font-mono text-xs text-indigo-600">{d.code}</span>
              <span className="truncate">{d.label.replace(/^\d{2} - /, "")}</span>
              {d.count > 0 && <span className="ml-auto shrink-0 text-xs text-slate-400">{d.count} fiche{d.count > 1 ? "s" : ""}</span>}
            </label>
          ))}
          {choices.divisions.length > 0 && visible.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-slate-400">Aucune division ne correspond au filtre.</p>
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-2 border-t border-slate-200 pt-3">
        <input data-testid="activity-manual" value={manual} onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
          placeholder="Code NAF précis (ex. 56.10C)"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-mono text-sm outline-none focus:border-indigo-500" />
        <button type="button" onClick={addManual}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:border-slate-300">
          <Plus size={14} /> Ajouter
        </button>
      </div>
    </div>
  );
}
